import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../csv-export/csv-export/csv-column';
import { ZeitauswahlChange } from '../zeitauswahl/zeitauswahl-change';
import { FahrzeugKostenGesamt, FahrzeugKostenSummen } from './fahrzeugkosten-gesamt';

@Component({
    selector: 'kiene-fahrzeugkosten-report',
    templateUrl: './fahrzeugkosten-report.component.html',
    styleUrls: ['./fahrzeugkosten-report.component.scss'],
})
export class FahrzeugkostenReportComponent implements OnInit, AfterViewInit {
    displayedColumns = [
        'kennzeichen',
        'bezeichnung',
        'tierarzt',
        'kostenstelle_suffix',
        'anteil_prozent',
        'netto_4510',
        'netto_4520',
        'netto_4530',
        'netto_4540',
        'netto_4580',
        'netto_2120',
        'netto_4832',
        'netto_sonst',
        'netto_gesamt',
        'belege',
    ];

    displayedDetailColumns: string[] = [];
    displayedAbgleichColumns = [
        'abgleich_kennzeichen',
        'abgleich_bezeichnung',
        'abgleich_tierarzt',
        'abgleich_netto_gesamt',
        'abgleich_abgabebelege_anzahl',
        'abgleich_abgabebelege_umsatz',
        'abgleich_kosten_je_abgabebeleg',
        'abgleich_umsatz_je_abgabebeleg',
        'abgleich_kfz_am_umsatz_prozent',
    ];

    dataSource = new MatTableDataSource<FahrzeugKostenGesamt>([]);
    detailDataSource = new MatTableDataSource<FahrzeugKostenGesamt>([]);
    abgleichDataSource = new MatTableDataSource<FahrzeugKostenGesamt>([]);
    @ViewChild('hauptSort') sort: MatSort;
    @ViewChild('detailSort') detailSort: MatSort;
    @ViewChild('abgleichSort') abgleichSort: MatSort;

    private allRecords: FahrzeugKostenGesamt[] = [];
    zeiteintrag: ZeitauswahlChange;
    summen: FahrzeugKostenSummen | null = null;
    loading = false;
    sub: Subscription;
    nurFreigegebenCtrl = new FormControl(false);
    auchOhneKostenCtrl = new FormControl(false);
    nurAktivCtrl = new FormControl(false);
    verteilenCtrl = new FormControl(true);
    tageweiseCtrl = new FormControl(true);
    leistungsdatumCtrl = new FormControl(true);
    ver02TextCtrl = new FormControl(true);
    afaTextCtrl = new FormControl(true);
    kennzeichenKommentarCtrl = new FormControl(true);
    dieselCtrl = new FormControl(true);
    superCtrl = new FormControl(true);
    adblueCtrl = new FormControl(true);
    betriebSonstCtrl = new FormControl(true);

    chartType = ChartType.PieChart;
    chartColumns = ['Fahrzeug', 'Kosten'];
    chartData: (string | number)[][] = [['', 0]];
    chartHasData = false;

    csvColumns: CsvColumn[] = [];
    csvFilename = 'Fahrzeugkosten.csv';
    csvDetailColumns: CsvColumn[] = [];
    csvDetailFilename = 'Fahrzeugkosten-Betrieb-4530.csv';
    csvAbgleichColumns: CsvColumn[] = [];
    csvAbgleichFilename = 'Fahrzeugkosten-Abgabebelege.csv';

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.csvColumns = [
            new CsvColumn('kennzeichen', 'Kennzeichen'),
            new CsvColumn('bezeichnung', 'Bezeichnung'),
            new CsvColumn('tierarzt', 'Tierarzt'),
            new CsvColumn('kostenstelle_suffix', 'KST-Suffix'),
            new CsvColumn('anteil_prozent', 'Anteil (%)', '%'),
            new CsvColumn('netto_4510', '4510 Steuer'),
            new CsvColumn('netto_4520', '4520 Versicherung'),
            new CsvColumn('netto_4530', '4530 Betrieb'),
            new CsvColumn('netto_4540', '4540 Reparatur'),
            new CsvColumn('netto_4580', '4580 Sonstige/Connectivity'),
            new CsvColumn('netto_2120', '2120 Zinsen'),
            new CsvColumn('netto_4832', '4832 AfA Fahrzeuge'),
            new CsvColumn('netto_sonst', 'Sonstige Konten'),
            new CsvColumn('netto_gesamt', 'Gesamt'),
            new CsvColumn('belege', 'Belege'),
            new CsvColumn('positionen', 'Positionen'),
        ];
        this.csvDetailColumns = [
            new CsvColumn('kennzeichen', 'Kennzeichen'),
            new CsvColumn('bezeichnung', 'Bezeichnung'),
            new CsvColumn('kostenstelle_suffix', 'KST-Suffix'),
            new CsvColumn('netto_4530', '4530 Betrieb'),
            new CsvColumn('netto_4530_diesel', 'Diesel'),
            new CsvColumn('netto_4530_super', 'Super'),
            new CsvColumn('netto_4530_adblue', 'AdBlue'),
            new CsvColumn('netto_4530_sonst', 'Betrieb sonst.'),
        ];
        this.csvAbgleichColumns = [
            new CsvColumn('kennzeichen', 'Kennzeichen'),
            new CsvColumn('bezeichnung', 'Bezeichnung'),
            new CsvColumn('tierarzt', 'Tierarzt'),
            new CsvColumn('netto_gesamt', 'Kfz-Kosten'),
            new CsvColumn('abgabebelege_anzahl', 'Abgabebelege'),
            new CsvColumn('abgabebelege_umsatz', 'Umsatz Abgabebelege'),
            new CsvColumn('kosten_je_abgabebeleg', 'Kfz je Beleg'),
            new CsvColumn('umsatz_je_abgabebeleg', 'Umsatz je Beleg'),
            new CsvColumn('kfz_am_umsatz_prozent', 'Kfz % vom Umsatz', '%'),
        ];
        this.rebuildDetailColumns();
        this.nurFreigegebenCtrl.valueChanges.subscribe(() => this.loadData());
        this.auchOhneKostenCtrl.valueChanges.subscribe(() => this.applyFilter());
        this.nurAktivCtrl.valueChanges.subscribe(() => this.loadData());
        this.verteilenCtrl.valueChanges.subscribe(() => this.loadData());
        this.tageweiseCtrl.valueChanges.subscribe(() => this.loadData());
        this.leistungsdatumCtrl.valueChanges.subscribe(() => this.loadData());
        this.ver02TextCtrl.valueChanges.subscribe(() => this.loadData());
        this.afaTextCtrl.valueChanges.subscribe(() => this.loadData());
        this.kennzeichenKommentarCtrl.valueChanges.subscribe(() => this.loadData());
        this.dieselCtrl.valueChanges.subscribe(() => this.rebuildDetailColumns());
        this.superCtrl.valueChanges.subscribe(() => this.rebuildDetailColumns());
        this.adblueCtrl.valueChanges.subscribe(() => this.rebuildDetailColumns());
        this.betriebSonstCtrl.valueChanges.subscribe(() => this.rebuildDetailColumns());
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (row, col) => {
            const v = (row as any)[col];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
        this.detailDataSource.sort = this.detailSort;
        this.detailDataSource.sortingDataAccessor = (row, col) => {
            const key = col.startsWith('detail_') ? col.substring('detail_'.length) : col;
            const v = (row as any)[key];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
        this.abgleichDataSource.sort = this.abgleichSort;
        this.abgleichDataSource.sortingDataAccessor = (row, col) => {
            const key = col.startsWith('abgleich_') ? col.substring('abgleich_'.length) : col;
            const v = (row as any)[key];
            return typeof v === 'string' ? v.toLowerCase() : v;
        };
    }

    zeitintervalChanged(event: ZeitauswahlChange): void {
        this.zeiteintrag = event;
        this.loadData();
    }

    loadData(): void {
        if (!this.zeiteintrag?.von || !this.zeiteintrag?.bis) {
            return;
        }
        let params = new HttpParams();
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
        params = params.set('nur_freigegeben', this.nurFreigegebenCtrl.value ? '1' : '0');
        params = params.set('nur_aktiv', this.nurAktivCtrl.value ? '1' : '0');
        params = params.set('verteilen', this.verteilenCtrl.value ? '1' : '0');
        params = params.set('tageweise', this.tageweiseCtrl.value ? '1' : '0');
        params = params.set('leistungsdatum', this.leistungsdatumCtrl.value ? '1' : '0');
        params = params.set('ver02_text', this.ver02TextCtrl.value ? '1' : '0');
        params = params.set('afa_text', this.afaTextCtrl.value ? '1' : '0');
        params = params.set('kennzeichen_kommentar', this.kennzeichenKommentarCtrl.value ? '1' : '0');

        this.loading = true;
        this.sub?.unsubscribe();
        this.sub = this.api.get(this.apiBaseUrl + 'report/read_fahrzeug_gesamt.php', params).subscribe({
            next: (res: any) => {
                this.allRecords = res?.records ?? [];
                this.summen = res?.summen ?? null;
                this.applyFilter();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('Fahrzeugkosten konnten nicht geladen werden.', err);
            },
        });
    }

    private applyFilter(): void {
        const showAll = !!this.auchOhneKostenCtrl.value;
        this.dataSource.data = showAll
            ? this.allRecords
            : this.allRecords.filter(r => (Number(r.netto_gesamt) || 0) > 0);

        this.detailDataSource.data = this.dataSource.data.filter(r => (Number(r.netto_4530) || 0) > 0);
        this.abgleichDataSource.data = showAll
            ? this.allRecords
            : this.allRecords.filter(
                  r => (Number(r.netto_gesamt) || 0) > 0 || (Number(r.abgabebelege_anzahl) || 0) > 0,
              );

        this.chartData = [];
        for (const r of this.dataSource.data) {
            const n = Math.abs(Number(r.netto_gesamt) || 0);
            if (n <= 0) {
                continue;
            }
            const label = r.kennzeichen || r.anzeige || String(r.fibu_fahrzeug_id);
            this.chartData.push([label, n]);
        }
        this.chartHasData = this.chartData.length > 0;
        if (!this.chartHasData) {
            this.chartData = [['Keine Kosten', 0]];
        }
    }

    private rebuildDetailColumns(): void {
        const cols = ['detail_kennzeichen', 'detail_bezeichnung', 'detail_kostenstelle_suffix', 'detail_netto_4530'];
        if (this.dieselCtrl.value) {
            cols.push('detail_netto_4530_diesel');
        }
        if (this.superCtrl.value) {
            cols.push('detail_netto_4530_super');
        }
        if (this.adblueCtrl.value) {
            cols.push('detail_netto_4530_adblue');
        }
        if (this.betriebSonstCtrl.value) {
            cols.push('detail_netto_4530_sonst');
        }
        this.displayedDetailColumns = cols;
    }
}
